import React, { useState } from 'react';
import { Link, useSearchParams, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../hooks/useAuth';
import { CheckCircle, Lock } from '@phosphor-icons/react';
import { Paper, PasswordInput, Button, Text, Title, Anchor, Alert, Stack, ThemeIcon } from '@mantine/core';
import Logo from '../components/Logo';
import SEO from '../components/SEO';

const labelStyles = {
  label: { fontSize: '0.7rem', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.05em', color: 'var(--mantine-color-gray-5)', marginBottom: 4 },
};

export default function ResetPasswordPage() {
  const { t } = useTranslation();
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);
  const [loading, setLoading] = useState(false);
  const { resetPassword } = useAuth();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const token = searchParams.get('token');
  const uid = searchParams.get('uid');

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    if (password.length < 8) { setError(t('auth.reset.errorMin8')); return; }
    if (password !== confirmPassword) { setError(t('auth.reset.errorMismatch')); return; }
    setLoading(true);
    try {
      await resetPassword(uid, token, password);
      setSuccess(true);
      setTimeout(() => navigate('/logowanie'), 3000);
    } catch {
      setError(t('auth.reset.errorFailed'));
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <SEO title={t('auth.reset.seoTitle')} path="/reset-hasla" noIndex />
      <div className="min-h-screen bg-[#F6F5F4] flex items-center justify-center px-4">
        <div className="w-full max-w-sm">
          <div className="text-center mb-6">
            <Link to="/" className="inline-flex items-center mb-4">
              <Logo className="h-12" />
            </Link>
            <Title order={2} className="text-gray-900">{t('auth.reset.title')}</Title>
            <Text size="sm" c="dimmed" mt={4}>{t('auth.reset.subtitle')}</Text>
          </div>

          <Paper shadow="xs" p="xl" radius="md" withBorder>
            {success ? (
              <Stack align="center" gap="sm">
                <ThemeIcon size={48} radius="xl" color="green" variant="light">
                  <CheckCircle size={24} weight="duotone" />
                </ThemeIcon>
                <Title order={4}>{t('auth.reset.successTitle')}</Title>
                <Text size="sm" c="dimmed" ta="center">{t('auth.reset.successText')}</Text>
                <Anchor component={Link} to="/logowanie" size="sm" c="brand" mt="sm">{t('auth.reset.loginNow')}</Anchor>
              </Stack>
            ) : (
              <form onSubmit={handleSubmit} className="space-y-4">
                {error && (
                  <Alert color="red" variant="light" radius="md">
                    <Text size="xs">{error}</Text>
                  </Alert>
                )}
                {(!token || !uid) && (
                  <Alert color="yellow" variant="light" radius="md">
                    <Text size="xs">{t('auth.reset.noToken')}</Text>
                  </Alert>
                )}

                <PasswordInput
                  label={t('auth.reset.newPasswordLabel')}
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  leftSection={<Lock size={16} />}
                  placeholder={t('auth.passwordMin8')}
                  required
                  size="sm"
                  styles={labelStyles}
                />

                <PasswordInput
                  label={t('auth.reset.confirmLabel')}
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  leftSection={<Lock size={16} />}
                  placeholder={t('auth.reset.confirmPlaceholder')}
                  required
                  size="sm"
                  styles={labelStyles}
                />

                <Button
                  type="submit"
                  fullWidth
                  color="brand"
                  loading={loading}
                  disabled={!token || !uid}
                  styles={{ root: { fontWeight: 700, fontSize: 15, height: 42 } }}
                >
                  {t('auth.reset.submit')}
                </Button>
              </form>
            )}
          </Paper>
        </div>
      </div>
    </>
  );
}
